import React from "react";
import { ICustomerAddress } from "../../../Interfaces/Customer/ICustomerAddress";
import { Grid, Paper, TextField, Switch, FormControlLabel } from "@mui/material";
import StateSelector from "../../StateSelector";
import { isValidDate, nullOrPositiveNumber } from "../../../utils/utils";
import {getYearsAndMonthsElapsedLabelText, PhoneInput, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";
import {DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";

interface ICustomerAddressProps {
  address?: ICustomerAddress;
  onUpdateAddress(address: ICustomerAddress): void;
  errors?: Error[];
  disabled?: boolean;
  applicantAddressStateIdsToFilterBy: string[]; // list of state ids to filter by
  applicationDate: Date;
  style?: React.CSSProperties;
}

const AddressCard: React.FC<ICustomerAddressProps> = (props) => {  
  const { address } = props;
  
  const timeElapsedText = address
    ? getYearsAndMonthsElapsedLabelText(address.startDate, address.endDate, address.default, props.applicationDate, address.yearsLived, address.monthsLived)
    : '';
  const timeElapsedLabelText = `Start Date ${timeElapsedText}`;
  
  const handleUpdate = <T extends {} | null>(value: T, fieldName: string): void => {
    if (props.address) {
      const updatedAddress: ICustomerAddress = {
        ...props.address!,
        state: {
          ...props.address!.state
        }
      };
      updatedAddress[fieldName] = value;
      if (updatedAddress.default) {
        // If it's a current address, the end date is empty
        updatedAddress.endDate = undefined;
      }
      props.onUpdateAddress(updatedAddress);
    }
  };

  return (
    <>
      {address ? (
        <>
          <Paper style={props.style}>
            <Grid style={{ margin: 5 }} container spacing={1}>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "street1")
                  }
                  value={address.street1 ?? ""}
                  label="Street 1"
                  error={props.errors !== undefined && props.errors.find((e) => e.name === 'Street') !== undefined}
                  helperText={props.errors?.find((e) => e.name === 'Street')?.message}
                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "street2")
                  }
                  value={address.street2 ?? ""}
                  label="Street 2"

                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value?.substring(0, 10), "apartmentNumber")
                  }
                  value={address.apartmentNumber ?? ""}
                  label="Apartment Number"

                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "city")
                  }
                  value={address.city || ""}
                  label="City"
                  error={props.errors !== undefined && props.errors.find((e) => e.name === 'City') !== undefined}
                  helperText={props.errors?.find((e) => e.name === 'City')?.message}

                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <StateSelector
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  showLabel
                  value={address.stateId || ""}
                  onChange={(val) => handleUpdate<string>(val, "stateId")}
                  DefaultStateId={address.stateId}
                  error={props.errors !== undefined && props.errors.find((e) => e.name === 'State') !== undefined}
                  helperText={props.errors?.find((e) => e.name === 'State')?.message}
                  applicantAddressStateIdsToFilterBy={props.applicantAddressStateIdsToFilterBy}
                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  value={address.zipCode || ""}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "zipCode")
                  }
                  label="Zip"
                  error={props.errors !== undefined && props.errors.find((e) => e.name === 'Zip') !== undefined}
                  helperText={props.errors?.find((e) => e.name === 'Zip')?.message}

                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  value={address.county || ""}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "county")
                  }
                  label="County"

                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  value={address.township || ""}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "township")
                  }
                  label="Township"

                />
              </Grid>

              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  value={address.landlordName || ""}
                  onChange={(e) =>
                    handleUpdate<string>(e.currentTarget.value, "landlordName")
                  }
                  label="Mort. Holder / Landlord Name"

                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <PhoneInput
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '100%', backgroundColor: 'white', border: 'none' }}
                  value={address.landlordPhone || ""}
                  label="Landlord Phone"
                  onChangeValue={(val) =>
                    handleUpdate<string>(val, "landlordPhone")
                  }
                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <TextField
                  disabled={props.disabled}
                  fullWidth
                  style={{ width: '95%' }}
                  label="Monthly Payment"
                  value={address.rentMortgagePayment || ""}
                  onChange={(e) =>
                    handleUpdate<number | null>(
                      nullOrPositiveNumber(e.currentTarget.value),
                      "rentMortgagePayment"
                    )
                  }

                  type="number"
                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12} container alignItems='center'>
                <FormControlLabel
                  disabled={props.disabled}
                  label="Current Address"
                  control={
                    <Switch
                      size="small"
                      color="primary"
                      checked={address.default}
                      onChange={((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                        handleUpdate<boolean>(
                          checked,
                          "default"
                        );
                      })}
                    />
                  }
                />
              </Grid>

              <Grid item lg={3} md={4} sm={6} xs={12} alignItems="flex-start" container>
                <DatePickerWithYearSanitizer
                  disabled={props.disabled}
                  label={timeElapsedLabelText}
                  value={isValidDate(address.startDate) ? new Date(address.startDate!) : null}
                  onChange={(event: DatePickerChangeEvent) => handleUpdate(event.value, "startDate")}
                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <DatePickerWithYearSanitizer
                  disabled={props.disabled || address.default}
                    label="End Date"
                    value={isValidDate(address.endDate) ? new Date(address.endDate!) : null}
                    onChange={(event: DatePickerChangeEvent) => handleUpdate(event.value, "endDate")}
                  />
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : null}
    </>
  );
};

export default AddressCard;
